import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { supabase } from '@/src/services/supabaseClient';
import { db } from '../services/db';
import { User } from '../../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (data: { name: string; email: string; password?: string; role?: 'admin' | 'operator'; sector?: string; avatarUrl?: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_CACHE_KEY = 'asa_branca_user_cache';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const saveUserToCache = (user: User) => {
      try {
          localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
      } catch (e) {
          console.error('[Auth] Failed to cache user:', e);
      }
  };

  const clearUserCache = () => {
      localStorage.removeItem(USER_CACHE_KEY);
  };

  const loadUserFromCache = (): User | null => {
      try {
          const cached = localStorage.getItem(USER_CACHE_KEY);
          if (cached) {
              return JSON.parse(cached);
          }
      } catch (e) {
          console.error('[Auth] Failed to parse cached user:', e);
      }
      return null;
  };

  // Helper: Fetch profile with timeout
  const syncProfile = async (userId: string) => {
    console.log(`[Auth] Syncing profile for user: ${userId}`);
    try {
        // Create a timeout promise (5 seconds)
        const timeout = new Promise<null>((_, reject) =>
            setTimeout(() => reject(new Error('Profile sync timed out')), 5000)
        );

        // Race between DB call and timeout
        const profile = await Promise.race([
            db.getProfile(userId),
            timeout
        ]) as User | null;

        if (profile) {
            console.log(`[Auth] Profile synced: ${profile.name}`);
            if (mountedRef.current) {
                setUser(profile);
            }
            saveUserToCache(profile);
        } else {
            console.warn(`[Auth] Profile not found on server for: ${userId}`);
            // Do NOT logout here. Keep cached version if available.
        }
    } catch (error) {
        console.error(`[Auth] Error syncing profile:`, error);
        // Do NOT logout on network error.
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const initializeAuth = async () => {
        console.log("[Auth] Starting initialization...");

        // 1. Optimistic Load
        const cachedUser = loadUserFromCache();
        if (cachedUser) {
            console.log("[Auth] Loaded user from cache:", cachedUser.name);
            setUser(cachedUser);
            setLoading(false); // Immediate unlock
        }

        try {
            // 2. Background Validation
            const { data: { session }, error } = await supabase.auth.getSession();

            if (error) {
                console.error("[Auth] Session check error:", error);
            }

            if (session?.user) {
               console.log("[Auth] Session active, syncing profile...");

               if (cachedUser) {
                   // If we have cache, sync in background (fire and forget)
                   syncProfile(session.user.id);
               } else {
                   // If no cache, we must await to prevent flash, but we use the timeout version
                   await syncProfile(session.user.id);
               }
            } else {
               console.log("[Auth] No active session.");
               // Only clear if we really have no session
               if (!cachedUser) {
                   if (mountedRef.current) setLoading(false);
               } else {
                   // If we have cache but no session...
                   if (navigator.onLine) {
                       // If online and no session, token is likely dead.
                       // Ideally we should try to refresh or just let it be.
                       // For now, we trust the cache for "Offline First" experience unless explicit logout.
                   }
               }
            }
        } catch (error) {
            console.error("[Auth] Initialization unexpected error:", error);
        } finally {
            if (mountedRef.current && loading) {
                setLoading(false);
            }
        }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mountedRef.current) return;

      console.log(`[Auth] Auth state changed: ${event}`);

      if (event === 'SIGNED_IN' && session?.user) {
        // We do NOT set loading(true) here to avoid "Zombie Loading" on re-connections
        // We just sync the profile in the background.
        await syncProfile(session.user.id);
      } else if (event === 'SIGNED_OUT') {

        // CHECK: Network Grace Period
        if (!navigator.onLine) {
            console.warn("[Auth] SIGNED_OUT detected but offline. Ignoring to prevent accidental logout.");
            return;
        }

        setUser(null);
        clearUserCache();
        if (mountedRef.current) setLoading(false);
      } else if (event === 'TOKEN_REFRESHED') {
         console.log("[Auth] Token refreshed");
      }
    });

    return () => {
        mountedRef.current = false;
        subscription.unsubscribe();
    };
  }, []); // Remove dependencies to run once

  const login = async (email: string, password: string) => {
    setLoading(true);
    console.log(`[Auth] Attempting login for: ${email}`);
    try {
      const user = await db.authenticate(email, password);
      console.log("[Auth] Login successful");

      // Force refreshing the profile to get the latest needsPasswordReset flag
      const freshProfile = await db.getProfile(user.id);

      if (mountedRef.current) setUser(freshProfile || user);
      saveUserToCache(user);
    } catch (error) {
      console.error("[Auth] Login error:", error);
      throw error;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    console.log("[Auth] Logging out...");
    try {
      await db.signOut();
      if (mountedRef.current) setUser(null);
      clearUserCache();
      console.log("[Auth] Logout successful");
    } catch (e) {
        console.error("[Auth] Logout error:", e);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const signUp = async (data: any) => {
    setLoading(true);
    console.log(`[Auth] Signing up user: ${data.email}`);
    try {
       await db.createUser(data, user as User);
       console.log("[Auth] Signup successful");
    } catch (error) {
        console.error("[Auth] Signup error:", error);
        throw error;
    } finally {
        if (mountedRef.current) setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{
        user,
        loading,
        isAdmin: user?.role === 'admin',
        login,
        logout,
        signUp
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
